import React, { useState } from 'react';

import { formatNumber } from 'utils/helper';
import { useFormatDate } from 'utils/useFormatDate';

import { makeStyles } from '@material-ui/styles';
import {
  Avatar,
  Hidden,
  Card,
  Box,
  CardContent,
  Typography
} from '@material-ui/core';
import RouteIcon from '@mui/icons-material/Route';
import SpeedIcon from '@mui/icons-material/Speed';
import MovingIcon from '@mui/icons-material/Moving';
import BarChart from '@mui/icons-material/BarChart';
import RssFeed from '@mui/icons-material/RssFeed';
import BoltIcon from '@mui/icons-material/Bolt';

import Activity from 'views/Peloton/Club/Menu/StreamList/Activity';

const useStyles = makeStyles(theme => ({
  info: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'flex-start',
    gap: theme.spacing(1) + 'px',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
    // '& > *': {
    //   marginRight: '6px'
    // }
    '&:last-child': {
      marginBottom: 0
    }
  },
  titleSection: {
    display: 'flex',
    justifyContent: 'start',
    flexDirection: 'column',
    '& > h3': {
      fontSize: '16px',
      marginRight: '2px'
    },
    [theme.breakpoints.up('md')]: {
      '& > h3': {
        fontSize: '20px'
      }
    }
  },
  nick: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    //marginTop: theme.spacing(1),
    padding: '12px 12px 4px 12px',
    '& > *': {
      marginRight: '6px'
    }
  },
  cards: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {}
  },
  stravaLink: {
    color: '#FC4C02',
    fontSize: '12px',
    cursor: 'pointer'
  },
  cardMain: {
    padding: '8px',
    '&:last-child': {
      paddingBottom: '12px'
    },
    [theme.breakpoints.up('md')]: {
      padding: '16px'
    }
  },
  cardMap: {
    // width: '30%',
    textAlign: 'center',
    '&:last-child': {
      paddingBottom: '12px'
    }
  },
  cardData: {
    display: 'flex',
    gap: '4px'
  },
  cardDataText: {
    fontSize: '12px'
  }
}));

export default function StreamCard({
  card,
  handleClickProfile,
  PolylineToSVG
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activityId, setActivityId] = useState(0);

  const classes = useStyles();
  const { formatTimestamp } = useFormatDate();

  return (
    <>
      <Box>
        <Card className={classes.cards} key={card.id}>
          <Box
            className={classes.nick}
            style={{
              cursor: 'pointer'
            }}
            onClick={() =>
              handleClickProfile({ userNickname: card.displayName })
            }>
            <Avatar
              src={card.profileImage}
              style={{
                width: '26px',
                height: '26px',
                marginRight: '8px'
              }}
            />
            <Typography variant="h6" color="textPrimary">
              {card.displayName}
            </Typography>
          </Box>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
            <CardContent className={classes.cardMain}>
              <div className={classes.info}>
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'start',
                    gap: 8
                  }}>
                  <Hidden mdUp>
                    <Box style={{ width: 70 }}>
                      <PolylineToSVG
                        width={60}
                        height={60}
                        polyline={card.summaryPolyline}
                        style={{ marginRight: 8 }}
                      />
                    </Box>
                  </Hidden>
                  <Box className={classes.titleSection}>
                    <Typography
                      variant="h3"
                      style={{ marginBottom: '12px', cursor: 'pointer' }}
                      onClick={() => {
                        setActivityId(card.activityId);
                        setDialogOpen(true);
                      }}>
                      {card.activityName}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{
                          marginRight: '8px',
                          fontSize: '12px'
                        }}>
                        {formatTimestamp(
                          String(card.startTimestamp),
                          'Asia/Seoul'
                        )}
                      </Typography>
                      <a
                        color="secondary"
                        size="xs"
                        variant="text"
                        target="_blank"
                        href={`https://www.strava.com/activities/${card.activityId}`}
                        className={classes.stravaLink}>
                        View on Strava
                      </a>
                    </div>
                  </Box>
                </Box>
              </div>
              <div className={classes.info}>
                <Box className={classes.cardData}>
                  <RouteIcon />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.cardDataText}>
                    {`거리: ${(card.distance / 1000).toFixed(2)}km`}
                  </Typography>
                </Box>
                <Box className={classes.cardData}>
                  <SpeedIcon />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.cardDataText}>
                    {`평속: ${(card.averageSpeed * 3.6).toFixed(2)}km/h`}
                  </Typography>
                </Box>
                <Box className={classes.cardData}>
                  <MovingIcon />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.cardDataText}>
                    {`상승고도: ${formatNumber(card.totalElevationGain)}m`}
                  </Typography>
                </Box>
              </div>
              <div className={classes.info}>
                <Box className={classes.cardData}>
                  <RssFeed />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.cardDataText}>
                    {`강도: ${card.intensity}`}
                  </Typography>
                </Box>
                <Box className={classes.cardData}>
                  <BarChart />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.cardDataText}>
                    {`훈련량: ${card.tss}`}
                  </Typography>
                </Box>
                <Box className={classes.cardData}>
                  <BoltIcon />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.cardDataText}>
                    {`평균파워: ${card.averageWatts.toFixed(0)}w`}
                  </Typography>
                </Box>
              </div>
            </CardContent>
            <Hidden smDown>
              <CardContent className={classes.cardMap}>
                <PolylineToSVG
                  polyline={card.summaryPolyline}
                  width={125}
                  height={125}
                />
              </CardContent>
            </Hidden>
          </Box>
        </Card>
      </Box>
      {dialogOpen && activityId ? (
        <Activity
          activityId={activityId}
          handleCloseDialog={() => setDialogOpen(false)}
        />
      ) : null}
    </>
  );
}
